import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { adminAuth, formatApiErrorDetail } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ADMIN } from "@/constants/testIds";
import { LOGO_URL } from "@/content/veguk";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (adminAuth.isAuthenticated()) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await adminAuth.login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={ADMIN.loginRoot}
      className="min-h-screen flex items-center justify-center bg-[#F9F6F0] px-6 py-16"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <img
            src={LOGO_URL}
            alt="VegUK"
            className="h-14 w-14 rounded-full bg-white border border-[#D1C9B9] p-1"
          />
          <div>
            <div className="font-serif-display text-2xl font-bold text-[#2C352D]">VegUK</div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2E5C46]">
              Admin
            </div>
          </div>
        </div>
        <form
          onSubmit={onSubmit}
          className="bg-white border border-[#D1C9B9] rounded-lg p-8 shadow-[0_30px_60px_-30px_rgba(44,53,45,0.18)]"
        >
          <div className="flex items-center gap-2 text-[#2E5C46] mb-5">
            <Lock size={18} />
            <h1 className="font-serif-display text-2xl text-[#2E5C46]">Sign in</h1>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#2C352D]">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={ADMIN.loginEmail}
                className="mt-2 bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46]"
                placeholder="admin@veguk.org"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#2C352D]">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={ADMIN.loginPassword}
                className="mt-2 bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46]"
                placeholder="••••••••"
              />
            </div>
          </div>
          {error && (
            <p
              data-testid={ADMIN.loginError}
              className="mt-4 text-sm text-[#A34E36] bg-[#F5E9E6] border border-[#A34E36]/30 rounded-md p-3"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            data-testid={ADMIN.loginSubmit}
            disabled={submitting}
            className="mt-6 w-full bg-[#2E5C46] hover:bg-[#234836] text-[#F9F6F0] rounded-md py-6"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
          <p className="mt-6 text-center text-xs text-[#4A5D4E]">
            Authorised personnel only. All access is logged.
          </p>
        </form>
      </div>
    </div>
  );
}
